import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box, Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton, Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm, UseFormGetValues, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { ROOM_CONFIGS_NAMES, ROOM_NAMES } from "@/lib/constants";
import { Delete as DeleteIcon } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const percentField = z.preprocess((val) => {
  if (val === "" || val === undefined || val === null) return undefined;
  return val;
},z.coerce
  .number({ invalid_type_error: "Value must be a number" })
  .min(0, "≥ 0")
  .max(100, "≤ 100"))

const stageSchema = z.object({
  min_prc: percentField,
  max_prc: percentField,
  prob: percentField,
}).superRefine((data, ctx) => {
  // Пропускаем если нет значений
  if (data.min_prc === undefined || data.max_prc === undefined) return;

  if (data.min_prc > data.max_prc) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Min cannot be greater than max",
      path: ["min_prc"],
    });
  }
});

const pokerTypeSchema = z.object({
  flop: stageSchema,
  turn: stageSchema,
});

const insuranceConfigSchema = z.object({
  name: z.string().min(1, "Required"),
  auth_groups: z.set(z.string()).min(1, "Min 1 element") /*.optional()*/,
  enabled: z.boolean().default(true),
  data: z.record(z.string(), pokerTypeSchema)
});

export type InsuranceConfigForm = z.infer<typeof insuranceConfigSchema>;

export function InsuranceCreateEditModal({
  open,
  mode,
  initialData,
  onSave,
  onCancel,
}: {
  open: boolean;
  mode?: "add" | "edit";
  initialData?: InsuranceConfigForm;
  onSave?: (data: InsuranceConfigForm) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [ addGameTypeModalIsOpen, setAddGameTypeModalIsOpen] = useState(false);
  const [ copyGameTypeDataModalIsOpen, setCopyGameTypeDataModalIsOpen] = useState(false);
  const [ copiableGameTypeName, setCopiableGameTypeName] = useState<string>("");


  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: open,
  });

  const authGroupsOptions = useMemo(
    () => authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [],
    [authGroupsRs?.data]
  );

  const { data: gameTypesRs, isLoading: gameTypesAreLoading } = useSysCfgQuery({
    domain: ROOM_NAMES.SERVICES,
    config_name: ROOM_CONFIGS_NAMES.GAME_TYPE,
  });

  const gameTypeOptions = useMemo(() => {
    return (
      gameTypesRs?.data.content
        ?.filter((item) => item.enabled)
        .map((item) => item.name)
        .sort((a, b) => a.localeCompare(b)) ?? []
    );
  }, [gameTypesRs]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues
  } = useForm<InsuranceConfigForm>({
    mode: "onChange",
    resolver: zodResolver(insuranceConfigSchema),
  });

  useEffect(() => {
    reset(
      initialData ?? {
        name: "",
        auth_groups: new Set(),
        enabled: true,
        data: {
        },
      }
    );
  }, [open, initialData]);

  const onSubmit: SubmitHandler<InsuranceConfigForm> = (data) => {
    onSave?.(data);
    onCancel?.();
  };

  const handleRemoveGameTypeFromFilling = (removableGameType: string) => {
    const currentData = getValues("data");

    const updatedData = { ...currentData };
    delete updatedData[removableGameType];

    setValue("data", updatedData);
  };


  const renderStageFields = (base: string) => (
    <Grid container spacing={1}>
      {["min_prc", "max_prc", "prob"].map((field) => (
        <Grid item xs={4} key={field}>
          <Controller
            name={`${base}.${field}` as any}
            control={control}
            render={({ field: f, fieldState: { error } }) => (
              <TextField
                {...f}
                type="number"
                label={
                  field === "min_prc"
                    ? t("Min %")
                    : field === "max_prc"
                    ? t("Max %")
                    : t("Probability")
                }
                placeholder={
                  field === "min_prc"
                    ? t("Enter min%")
                    : field === "max_prc"
                    ? t("Enter max%")
                    : t("Enter probability")
                }
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
      ))}
    </Grid>
  );

  const renderPokerSection = (label: string, key: keyof InsuranceConfigForm["data"]) => (
    <Box
      key={key}
      sx={{
        border: "1px solid #eee",
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {label.toUpperCase()}
        </Typography>
        <div>
          <IconButton
            size="small"
            onClick={() => {
              setCopiableGameTypeName(label)
              setCopyGameTypeDataModalIsOpen(true)
            }}
            sx={{ position: "inherit", right: 18, top: 8 }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleRemoveGameTypeFromFilling(key)}
            sx={{ position: "inherit", right: 18, top: 8 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      </div>


      <Grid container spacing={2}>

        {["flop", "turn"].map((stage) => (
          <Grid item xs={6} key={stage}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t(stage.charAt(0).toUpperCase() + stage.slice(1))}
            </Typography>
            {renderStageFields(`data.${key}.${stage}`)}
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        ) {
          return;
        }
        onCancel?.();
      }}
    >
      <DialogTitle>
        {mode === "edit" ? t("edit") : t("createNewInsurance")}
        <IconButton onClick={onCancel} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}
        >
          {/* name */}
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("name")}
                fullWidth
                error={!!error}
                helperText={error?.message}
                slotProps={{ input: { disabled: mode === "edit" } }}
              />
            )}
          />


          <Controller
            name="auth_groups"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                multiple
                options={authGroupsOptions}
                loading={authGroupsIsLoading}
                value={[...value]}
                onChange={(_, newValue) => onChange(new Set(newValue))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("authGroups")}
                    fullWidth
                    error={!!errors.auth_groups}
                    helperText={errors.auth_groups?.message}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {authGroupsIsLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />

          {/* enabled */}
          {mode === "add" && (
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label={t("enabled")}
                />
              )}
            />)}

          <Divider sx={{ my: 2 }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3>Game Types</h3>
            <Button
              variant="outlined"
              onClick={() => {
                setAddGameTypeModalIsOpen(true);
              }}
            >
              Add Game Type
            </Button>
          </div>



          <Grid container spacing={2}>
            {Object.keys(watch("data") ?? {}).map((gameTypeName) => (  //Написано так, потому что криво рендерится сет полей при копировании
              <>
                {renderPokerSection(gameTypeName, gameTypeName as keyof InsuranceConfigForm["data"])}
              </>

            ))}
          </Grid>

          <ModalActionsButtons
            onCancelClick={() => onCancel?.()}
            onConfirmClick={() => void 0}
          />
        </form>

        <AddGameTypeToFormModal
          open={addGameTypeModalIsOpen}
          allAvailableGameTypes={gameTypeOptions.filter(value =>
            !Object.keys(watch("data") ?? {}).includes(value)
          )}
          onSave={(addableGameTypes => {
            const newData = { ...getValues("data") };

            // Добавляем каждый новый тип игры с пустой структурой
            addableGameTypes.forEach(gameType => {
              newData[gameType] = { flop: {min_prc: 0, max_prc: 0, prob: 0}, turn: {min_prc: 0, max_prc: 0, prob: 0} };
            });

            setValue("data", newData, { shouldDirty: true, shouldTouch: true });
            setAddGameTypeModalIsOpen(false)
          })}
          onCancel={() => setAddGameTypeModalIsOpen(false)}
        />

        <AddGameTypeToFormModal
          open={copyGameTypeDataModalIsOpen}
          allAvailableGameTypes={gameTypeOptions ?? []}
          onSave={(addableGameTypes => {
            const newData = { ...getValues("data") };

            // Добавляем каждый новый тип игры с пустой структурой
            addableGameTypes.forEach(gameType => {
              setValue(`data.${gameType}`, { flop: { ...(newData[copiableGameTypeName]?.flop) }, turn: {...(newData[copiableGameTypeName]?.turn)} }, { shouldDirty: true, shouldTouch: true });
              //newData[gameType] = { flop: { ...(newData[copiableGameTypeName]?.flop) }, turn: {...(newData[copiableGameTypeName]?.turn)} };
            });

            //setValue("data", newData, { shouldDirty: true, shouldTouch: true });
            setCopyGameTypeDataModalIsOpen(false)
          })}
          onCancel={() => setCopyGameTypeDataModalIsOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

const AddGameTypeToFormModal = ({
                     open,
                     allAvailableGameTypes,
                     onSave,
                     onCancel,
                   }: {
  open: boolean;
  allAvailableGameTypes: string[],
  onSave: (addableGameTypes: string[]) => void;
  onCancel: () => void;
}) => {

  const [selectedGameTypes, setSelectedGameTypes] = useState<string[]>([]);

  /*useEffect(() => {
    if (open && initialData) reset(initialData);
  }, [initialData, open, reset]);*/

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
        Choose Game Type
        <IconButton onClick={onCancel} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
        <Autocomplete
          multiple
          options={allAvailableGameTypes}
          onChange={(event, newValues) => setSelectedGameTypes(newValues)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Game Types"
              fullWidth
              style={{marginTop: 8}}
              InputProps={{
                ...params.InputProps,
              }}
            />
          )}
        />
        <ModalActionsButtons
          onCancelClick={onCancel}
          onConfirmClick={() => onSave(selectedGameTypes)}
        />
      </DialogContent>
    </Dialog>
  );
};

